import { advanceWalkPhase } from '../engine/animation'
/**
 * Enemy, pickup, projectile and particle entities.
 *
 * Every kind below is a class extending `EntityBase`, registered at the bottom
 * of this file, so simply importing this module is all it takes for
 * `level.spawn('rivet', x, y)` to work. Behaviour that belongs to a *placed
 * object* (crate blocks, bricks, cannons) lives in `behaviours.ts`; this
 * file owns the things that move on their own once released.
 *
 * Conventions shared by every class here:
 *   - `x`/`y` is the top-left of the hitbox, in native pixels; one tile = 16px.
 *   - Speeds are native pixels per 1/60s frame, matching the rest of the engine.
 *   - `data` holds per-instance animation and state-machine fields. The
 *     renderer reads them, and they survive because they live on the entity
 *     rather than being baked into the class.
 *   - `update` steps anything that walks through the local `step()` helper, so
 *     terrain (and placed solid objects) stops it. Wisps and particles skip
 *     that and are integrated by hand.
 *   - `step()` adds ground-snapping on top of `EntityBase.physicsStep`; see its
 *     comment for why a standing walker must not be allowed to sink a pixel.
 */

import { sfxCoin, sfxPower, sfxStomp } from '../core/audio'
import { TILE } from '../engine/constants'
import { spawnHudPopup } from '../engine/hud'
import type { SolidQuery } from '../engine/collision'
import { EntityBase, type Entity } from '../engine/entity'
import { PHYS } from '../engine/physics'
// The payout values and the prism's window are shared with the *placed* pickups
// in this file's sibling: an expansion-cell released by a crate block and one
// placed on the floor are one pickup, and must be worth the same.
import { SCORE, SHIELD_FRAMES } from './behaviours'
import { registerEntity } from './level'
import type { LevelRuntime, PlayerLike } from './types'

// ── tuning ─────────────────────────────────────────────────────────────────

/** Ground acceleration shared by every walker. */
const WALK_GRAVITY = 0.3
/** Ground drag shared by every walker. */
const WALK_FRICTION = 0.02

const RIVET_SPEED = 0.35
const SCOUT_SPEED = 0.3
const SHELL_SPEED = 4
const SHARDLING_SPEED = 0.35

/** How far a wisp notices the player, and how far it loses interest again. */
const WISP_WAKE_X = TILE * 5
const WISP_WAKE_Y = TILE * 5
const WISP_SLEEP_X = TILE * 8
const WISP_SPEED = 0.6
/**
 * How fast a Wisp glides back to its post, and how close counts as having got
 * there.
 *
 * The return speed is the old aimless-drift speed on purpose: losing interest
 * should read as a retreat, not as a second charge, so it is never faster than
 * the sleep-walk it replaces. `WISP_HOME` is a fraction of a pixel rather than a
 * tile, because the wisp has to actually come to a stop — a tile-wide
 * tolerance would let it sit visibly off its post forever, or oscillate around
 * it once the clamp is tight enough to stop the motion.
 */
const WISP_DRIFT = 0.2
const WISP_HOME = 0.5
/** Free-running counter the renderer uses for the float bob. */
const WISP_PHASE = 60

/**
 * Which way a wisp faces while it sits on its post: the sprite is drawn facing
 * left, and -1 is the default the `wisp` registry entry declares as its `dir`
 * prop, so a placement that expresses no opinion and one that expresses an
 * unusable opinion agree. See `restingDir` for why the coercion that used to sit
 * here was worth removing.
 */
const WISP_RESTING_DIR = -1

/** Frames a ? Block pickup spends rising, and px per frame (16 x 1 = a tile). */
const EMERGE_FRAMES = 16
const EMERGE_SPEED = 1
const EXPANSION_SPEED = 0.5
const PRISM_SPEED = 0.6
const PRISM_HOP = -3
const EMBER_CELL_PHASE = 48
/** Coin bob: a full sine period, in frames, and the peak offset in px. */
const COIN_PERIOD = 48
const COIN_BOB = 1
const COIN_POP_LAUNCH = -4
const COIN_POP_GRAVITY = 0.35
const COIN_POP_LIFE = 30

const FIREBALL_SPEED = 3
const FIREBALL_BOUNCE = -2.2
const FIREBALL_BOUNCES = 2
const FIREBALL_LIFE = 180

const DEBRIS_GRAVITY = 0.45
const DEBRIS_LAUNCH = -2.5
const DEBRIS_SPIN = 12
const DEBRIS_LIFE = 120

const SQUASH_LIFE = 30
const POOF_FRAMES = 12
const POOF_START = 16
const POOF_END = 32

// ── helpers ────────────────────────────────────────────────────────────────

/**
 * Read a number out of a `data` bag or a spawn-props bag, falling back when the
 * key is missing or holds something else. `data` is `Record<string, unknown>`,
 * so every field access needs a narrowing like this one.
 */
function num(bag: Record<string, unknown>, key: string, fallback = 0): number {
  const v = bag[key]
  return typeof v === 'number' ? v : fallback
}

/** Reverse an entity's facing. Takes `this` so call sites read as an action. */
function turnAround(e: { facing: 1 | -1 }): void {
  e.facing = e.facing === 1 ? -1 : 1
}

/**
 * The heading a wisp rests on, resolved from its placement's `dir` prop.
 *
 * Only `1` and `-1` are accepted, and anything else is reported before the
 * default is substituted. The line this replaces was
 * `num(props, 'dir', -1) >= 0 ? 1 : -1`, which is a sign coercion wearing a
 * heading's clothes: every value that was not a negative number became a real
 * direction, so `dir: 0` — the one value that means *no* direction — produced a
 * wisp facing right, and `dir: "left"` produced one facing left, both silently.
 * That is the same failure the wisp's missing-prop bug had, one level down: an
 * authored value accepted, stored, and then replaced by a plausible neighbour.
 *
 * The fallback is `WISP_RESTING_DIR`, which is also what `objects.ts` declares as
 * the registry default, so a placement that says nothing and a placement that
 * says something unusable now end up in the same visible state — minus the
 * silence in the second case.
 */
function restingDir(raw: unknown, x: number, y: number): 1 | -1 {
  if (raw === 1 || raw === -1) return raw
  if (raw !== undefined) {
    console.warn(
      `entity: wisp at ${Math.round(x / TILE)},${Math.round(
        y / TILE,
      )} has prop dir=${JSON.stringify(raw)}, which is not a heading; using ${
        WISP_RESTING_DIR
      }`,
    )
  }
  return WISP_RESTING_DIR
}

/**
 * One step of tile collision for a walker or projectile, with gravity.
 *
 * Gravity is *not* added here — `physicsStep` does it correctly now. The
 * ground-snap behaviour below is still this function's job, and it is
 * load-bearing rather than cosmetic: with a non-zero downward `dy` the
 * collision test never quite reaches the tile below, so a standing walker would
 * sink a fraction of a pixel per frame and snap back — a visible 1px shimmer.
 * `moveY` probes for ground when `dy` is 0, so `onGround` stays true and only a
 * walker that has genuinely left the floor (or a slope) starts to fall, one
 * frame later, exactly as in the originals. An upward velocity still gets
 * through, so hops and bounces are unaffected.
 */
function step(
  e: EntityBase,
  world: SolidQuery,
  gravity: number,
  friction: number,
): void {
  // Kill accumulated fall speed on the frame we are planted, so a walker that
  // has landed does not carry downward momentum into the next step. This is
  // what keeps `onGround` true on a standing enemy: without it the next frame
  // integrates a fresh gravity step, and whether that re-collides depends on
  // sub-pixel position rather than on the walker actually being supported.
  if (e.onGround && e.vy > 0) e.vy = 0
  e.physicsStep(world, gravity, friction)
}

/**
 * True when a wall is directly in front of the entity — either seen by an
 * exact (zero-lookahead) probe, or hit by the previous step. The second half
 * matters at the course edge, where the invisible wall one tile outside the
 * level cannot be seen by a zero-lookahead probe, and a fast mover would
 * otherwise grind against it instead of turning around.
 *
 * A mover fast enough to outrun a zero-lookahead probe must not use this to
 * decide a bounce: at 4px a frame it steps over the last two pixels of clear
 * ground, the step it takes collides, and the collision — not the probe — is
 * what it has to answer. `Scout` reads the wall off the step for that reason.
 * This is for movers that re-assert their own speed every frame, so losing it
 * to a collision costs them one frame and not their momentum.
 */
function wallInFace(e: EntityBase, world: SolidQuery): boolean {
  return e.wallAhead(world, 0) || (e.body?.onWall ?? 0) !== 0
}

/** The slice of `Level` that entity code needs but `LevelRuntime` hides. */
interface EntityBox {
  alive: boolean
  x: number
  y: number
  w: number
  h: number
  kind: string
}

interface EntityListHost {
  entities: EntityBox[]
}

/** The live entity list, for shell-versus-enemy overlap tests. */
function peers(level: LevelRuntime): EntityBox[] {
  return (level as unknown as EntityListHost).entities
}

/** Axis-aligned box overlap. */
function boxesOverlap(a: EntityBox, b: EntityBox): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  )
}

/**
 * Enemy kinds a hazard such as a sliding shell is allowed to destroy.
 *
 * Exported because the shell's own Shield Prism pass needs the same list, and two
 * copies of "which kinds are enemies" is two chances to disagree about whether
 * a coin is a thing you can destroy. The Shield Prism used to delete *anything*
 * it touched -- coins, poofs, and the player's own fireball, each paying 200
 * points -- which made the prism a scoring exploit rather than a weapon.
 */
export const ENEMY_KINDS: ReadonlySet<string> = new Set([
  'rivet',
  'scout',
  'wanderer',
  'shardling',
  'wisp',
])

/**
 * Entity kinds that can never hurt the player, however they are overlapped.
 *
 * These are effects and remains, not combatants: a stomp leaves a flattened
 * `rivet-squash` lying on the floor, and a player who walks over it touches it
 * on the very next frame. The overlap pass in `main.ts` treats anything outside
 * this set as hostile, so the corpse of the enemy you just stomped used to kill
 * you -- the "stomp it and die anyway" report. Keeping the list next to the
 * kinds it names is what stops a new effect kind from repeating that.
 */
export const HARMLESS_KINDS: ReadonlySet<string> = new Set([
  'debris',
  'coin-pop',
  'poof',
  'rivet-squash',
])

/**
 * Damage a player who ran into an enemy from the side.
 *
 * The concrete player implements `hurt()`, which is where the shield, the
 * post-hit invulnerability window and the shrink are decided. Probing for it is
 * what makes an enemy touch obey exactly the same rules as every other damage
 * source; before this, every enemy called `kill()` on a small player directly,
 * so the shield and the spawn grace did nothing and a big player was knocked
 * back without ever shrinking. The fallback keeps minimal `PlayerLike` doubles
 * working: no hurt verb, so a big player bounces and a small one dies.
 */
function hurtPlayer(player: PlayerLike): void {
  if (typeof player.hurt === 'function') {
    player.hurt()
    return
  }
  if (player.power === 'big') player.setVy(PHYS.stompBounce)
  else player.kill()
}

/** `Level` tracks score; `LevelRuntime` does not declare it, so narrow here. */
type ScoringLevel = Partial<LevelRuntime & { addScore(n: number): void }>

/** The counter block `Level` carries and `LevelRuntime` deliberately hides. */
interface LifeLevel {
  stats?: { lives: number }
}

/**
 * Optional player capabilities a pickup needs but `PlayerLike` does not declare:
 * fire power, the prism, and a life counter. The concrete `Player` has all of
 * them, so every one is *probed* rather than cast — the same defensive shape
 * `behaviours.ts` uses for the placed pickups, which are the same items.
 */
interface PlayerHooks {
  makeFire?(): void
  grantShield?(frames: number): void
  shieldTime?: number
  state?: string
  lives?: number
}

/** Award points if the host level happens to keep score. */
function addScore(level: LevelRuntime, points: number): void {
  const host = level as ScoringLevel
  if (typeof host.addScore === 'function') host.addScore(points)
}

/**
 * Pay for one enemy the player killed, whichever route killed it.
 *
 * Every caller marks the victim dead *first* and then calls this, so a single
 * death cannot be paid for twice: the sliding-shell loop and the prism pass in
 * `main.ts` both skip anything with `alive === false`.
 *
 * The figure comes from `SCORE.stomp` and never from the call site, which is the
 * whole point. A rivet under a stomp and the same rivet flattened by a shell
 * are one kill, so they must be worth the same — the same reasoning that keeps a
 * block-released expansion-cell and a placed expansion-cell from drifting apart. It also
 * means the shell does not carry a per-enemy table: a new enemy kind would
 * otherwise need the shell edited as well as the enemy, and the two would
 * quietly disagree.
 */
function awardKill(level: LevelRuntime, at?: { x: number; y: number }): void {
  addScore(level, SCORE.stomp)
  sfxStomp()
  if (at !== undefined) {
    spawnHudPopup(at.x + 8, at.y, `+${SCORE.stomp}`, '#fcfcfc')
  }
}

/**
 * True when the player is already big or fire, so an expansion-cell is worth points
 * rather than a second power-up. `power` is the documented `PlayerLike` contract
 * and is checked first; the concrete player also carries a wider `state` that
 * adds `'fire'`.
 *
 * This has to match `isBig` in `behaviours.ts`, and the reason is `grow()`:
 * `Player.grow()` is `setPower('big')`, so a fire Tavi who "grew" would be
 * demoted to big and lose the fire power the game otherwise makes unobtainable.
 */
function isBig(player: PlayerLike): boolean {
  if (player.power === 'big') return true
  const state = (player as PlayerHooks).state
  return state === 'big' || state === 'fire'
}

/** Hand the player fire power, falling back to growing if unsupported. */
function giveFire(player: PlayerLike): void {
  const h = player as PlayerHooks
  if (typeof h.makeFire === 'function') h.makeFire()
  else if (player.power === 'small') player.grow()
}

/** Grant the prism's window of invincibility, writing the field if there is none. */
function giveShield(player: PlayerLike): void {
  const h = player as PlayerHooks
  if (typeof h.grantShield === 'function') h.grantShield(SHIELD_FRAMES)
  else h.shieldTime = SHIELD_FRAMES
}

/**
 * Add a life. There is no `Level.addLife`; the counter lives in `Level.stats`,
 * which `LevelRuntime` does not declare, so it is narrowed here exactly as
 * `behaviours.ts`' `addLife` narrows it — the Spare Battery is the same item by either
 * route and has to be worth the same life.
 */
function addLife(level: LevelRuntime, player: PlayerLike): void {
  const stats = (level as LifeLevel).stats
  if (stats !== undefined) {
    stats.lives += 1
    return
  }
  const h = player as PlayerHooks
  if (typeof h.lives === 'number') h.lives += 1
}

/** Stamp the level-assigned uid onto a freshly constructed entity. */
function withUid<T extends Entity>(e: T, uid: number): T {
  e.uid = uid
  return e
}

// ── enemies ────────────────────────────────────────────────────────────────

/**
 * Rivet — the baseline walker: paces back and forth at a steady 0.35px a
 * frame, turning at walls and at the lip of any ledge. A stomp from above
 * flattens it (leaving a `rivet-squash` sprite behind) and pays `SCORE.stomp`,
 * which is what the stomp used to omit: stomping is the most ordinary thing a
 * player does and it was worth nothing at all. Side contact hurts.
 * Renderer: `rivet-walk1..3`, chosen by `data.anim`.
 */
class Rivet extends EntityBase {
  constructor(x: number, y: number) {
    super('rivet', x, y, 14, 14)
  }

  private get anim(): number {
    return (this.data.anim as number) ?? 0
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    const world = level
    if (this.wallAhead(world) || !this.groundAhead(world)) turnAround(this)
    const previousX = this.x
    this.vx = this.facing * RIVET_SPEED
    step(this, world, WALK_GRAVITY, WALK_FRICTION)
    this.data.anim = advanceWalkPhase(
      this.anim,
      this.x - previousX,
      this.onGround,
      2,
    )
  }

  onStomp(level: LevelRuntime, _player: PlayerLike): boolean {
    // Flattened onto the floor it was standing on; the sprite is wider and
    // shorter than the walker, so re-anchor it to the feet.
    level.spawn('rivet-squash', this.x - 1, this.y + this.h - 8)
    this.alive = false
    awardKill(level, this)
    return true
  }

  onTouch(_level: LevelRuntime, player: PlayerLike): void {
    hurtPlayer(player)
  }
}

/**
 * Scout — a shelled walker. It paces about until stomped, at which point it
 * collapses into a motionless shell and pays `SCORE.stomp` for the walker that
 * just died. A dormant shell is booted sideways by the player — which pays
 * nothing, because kicking is not a kill — and a sliding shell annihilates
 * every enemy it runs into, paying `SCORE.stomp` for each and ploughing
 * straight on through all of them. It ricochets off walls — off the collision
 * it actually hits, so it bounces at the last moment rather than stalling
 * against the first one. Stomping a shell never hurts the player — it just
 * stops the shell dead, for free.
 * Renderer: `scout-walk1..3` while walking (picked by `data.anim`),
 * `scout-wheel` once `data.mode === 'shell'`, flipped by `facing`.
 */
class Scout extends EntityBase {
  /** Scouts check for ledges; the wandering variant deliberately does not. */
  protected readonly turnsAtLedges: boolean

  constructor(kind: string, x: number, y: number, turnsAtLedges: boolean) {
    super(kind, x, y, 12, 14)
    this.turnsAtLedges = turnsAtLedges
    this.data.mode = 'walk'
  }

  /** True once the scout has been stomped into a shell. */
  protected get shell(): boolean {
    return this.data.mode === 'shell'
  }

  /** A shell that is actually moving, as opposed to lying dormant. */
  private get sliding(): boolean {
    return this.shell && this.vx !== 0
  }

  private get anim(): number {
    return (this.data.anim as number) ?? 0
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    const world = level
    if (this.shell) {
      // Read "loose" once, before the step. A blocked step zeroes `vx`, and
      // `vx === 0` is exactly what dormant means to a shell, so anything asked
      // after the step cannot tell a bouncing shell from one lying on the floor.
      const loose = this.sliding
      if (loose) this.slide(level)
      // Shells still fall down slopes and off ledges; only their own speed is
      // held constant, so a dormant shell is given no ground friction.
      step(this, world, WALK_GRAVITY, loose ? 0 : WALK_FRICTION)
      if (loose) {
        // A loose shell's speed belongs to the shell, not to the physics:
        // `moveX` zeroes `vx` on the collision, and a shell that lost its speed
        // to a wall used to lie there dead for good rather than bouncing -- the
        // one bounce a player can see coming was the one that never happened.
        //
        // The wall that turned it is the one it actually hit, read from the
        // step that just ran. A probe *before* the step cannot do this job: at
        // 4px a frame a zero-lookahead probe is beaten to every wall, it only
        // sees a column the shell has already entered, and answering from it as
        // well would turn the shell twice on the same wall.
        const blocked = (this.body?.onWall ?? 0) !== 0
        this.vx = this.facing * SHELL_SPEED
        if (blocked) this.reverse()
      }
      return
    }

    if (
      this.wallAhead(world) ||
      (this.turnsAtLedges && !this.groundAhead(world))
    ) {
      turnAround(this)
    }
    const previousX = this.x
    this.vx = this.facing * SCOUT_SPEED
    step(this, world, WALK_GRAVITY, WALK_FRICTION)
    this.data.anim = advanceWalkPhase(
      this.anim,
      this.x - previousX,
      this.onGround,
      2,
    )
  }

  /**
   * A sliding shell flattens any enemy it touches and keeps its heading.
   *
   * It used to bounce off the enemy as well as off the wall, and that is what
   * made the shell read as broken rather than as a weapon: a shell you kicked
   * right through a room flattened one rivet and came straight back at you, so
   * the move that was supposed to be the safe one was the one that hurt. The
   * bounce is also the *only* rebound in the game a player cannot see coming —
   * a wall is right there, but the enemy is already gone by the time the shell
   * turns round, so there is nothing left to read.
   *
   * Walls still ricochet, because that is a rebound the player can watch coming
   * and steer around; `update` is where that happens, off the collision the
   * step reports. See `Fireball` for the same reasoning applied to a projectile
   * that used to bounce back across its own thrower.
   *
   * Dying entities are reaped by the level at the end of the step, so the
   * `alive` flag is enough to avoid double kills — and it is set *before* the
   * payout for the same reason. Every overlapping enemy is flattened: the loop
   * cannot `break`, because a shell wide enough to overlap two enemies has to
   * deal with both, and the heading is untouched either way.
   */
  private slide(level: LevelRuntime): void {
    const me = this as unknown as EntityBox
    for (const e of peers(level)) {
      if (!e.alive || e === me || !ENEMY_KINDS.has(e.kind)) continue
      if (!boxesOverlap(me, e)) continue
      e.alive = false
      // The shell pays for the kill but does not price it: one flat
      // `SCORE.stomp` for whatever it hit, so this route and the stomp route
      // cannot disagree and the shell needs no table of its own.
      awardKill(level, this)
      level.spawn('poof', e.x + e.w / 2 - 8, e.y + e.h / 2 - 8)
    }
  }

  /** Kick the shell back the other way. */
  private reverse(): void {
    this.vx = -this.vx
    this.facing = this.vx > 0 ? 1 : -1
  }

  onStomp(level: LevelRuntime, _player: PlayerLike): boolean {
    if (!this.shell) {
      // Collapse: 12x14 becomes 12x12, so drop 2px to keep the feet planted.
      this.data.mode = 'shell'
      this.vx = 0
      this.vy = 0
      this.h = 12
      this.y += 2
      // Flattening the walker pays. The shell it leaves behind is worth nothing
      // on its own: it is the same enemy, and the originals pay for the stomp
      // that collapsed it rather than for the shell.
      awardKill(level, this)
      return true
    }
    // A shell is safe to land on, moving or not: a moving one is simply stopped,
    // and the dormant shell sprite is drawn in its neutral orientation. No
    // payout, because nothing died — the shell is still there to be kicked, and
    // paying here would let a player farm points by bouncing on one.
    this.vx = 0
    this.facing = 1
    return true
  }

  onTouch(_level: LevelRuntime, player: PlayerLike): void {
    if (!this.shell) {
      hurtPlayer(player)
      return
    }
    if (this.vx === 0) {
      // Dormant shell: boot it away from the player, in the direction they
      // approached from, so it never fires straight back at them.
      this.vx = player.x < this.x ? SHELL_SPEED : -SHELL_SPEED
      this.facing = this.vx > 0 ? 1 : -1
      return
    }
    // Sliding shell: side contact is a hit.
    hurtPlayer(player)
  }
}

/**
 * Wandering Scout — the same shell trick as the Wheel Scout, but it never looks
 * where it is going: ledges are ignored, so it cheerfully walks off them and
 * drops down the hole. That recklessness is the whole point of the variant.
 * Renderer: `scout-walk1..3`, then `scout-wheel`.
 */
class Wanderer extends Scout {
  constructor(x: number, y: number) {
    super('wanderer', x, y, false)
  }
}

/**
 * Shardling — a spiked walker that turns only at walls; ledges do not concern it.
 *
 * DEFINING RULE: a shardling is NOT stompable. Its spikes point up as well as out,
 * so there is no safe angle of approach and stomping it is exactly as painful
 * as walking into it. `onStomp` therefore deals the damage itself and returns
 * `false`, handing the interaction back to the player, which must not treat
 * the contact as a successful stomp. There is consequently no payout on the
 * stomp path — nothing died — but a shardling is still killable, by a sliding shell
 * or a prism, and pays for that through `awardKill` there.
 * Renderer: `shardling-walk1..3`, chosen by `data.anim`.
 */
class Shardling extends EntityBase {
  constructor(x: number, y: number) {
    super('shardling', x, y, 14, 14)
  }

  private get anim(): number {
    return (this.data.anim as number) ?? 0
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    const world = level
    if (this.wallAhead(world)) turnAround(this)
    const previousX = this.x
    this.vx = this.facing * SHARDLING_SPEED
    step(this, world, WALK_GRAVITY, WALK_FRICTION)
    this.data.anim = advanceWalkPhase(
      this.anim,
      this.x - previousX,
      this.onGround,
      2,
    )
  }

  /** See the class comment: the spikes are the point. Never a free stomp. */
  onStomp(level: LevelRuntime, player: PlayerLike): boolean {
    this.onTouch(level, player)
    return false
  }

  onTouch(_level: LevelRuntime, player: PlayerLike): void {
    hurtPlayer(player)
  }
}

/**
 * Wisp — a foe that ignores terrain completely: no gravity, no tile
 * collision, it simply flies. It sleeps on its post until the player wanders
 * within about five tiles, then charges straight at the player's centre, and
 * gives up — gliding back to the post it was placed on and settling there — if
 * the player escapes much further away. It is kept inside the course bounds at
 * all times.
 * Renderer: `wisp`, floated vertically by `data.phase` and flipped by `facing`.
 */
class Wisp extends EntityBase {
  constructor(x: number, y: number, props: Record<string, unknown>) {
    super('wisp', x, y, 14, 14)
    this.data.dir = restingDir(props['dir'], x, y)
    this.data.awake = false
    this.data.phase = 0
    // The post it was placed on, recorded in `data` next to `dir` and `phase`
    // rather than as a class field: every other piece of per-instance state here
    // lives in that bag, and this is per-instance state like the rest. Without
    // it there is nowhere to retreat *to*, which is the whole bug this fixes.
    this.data.homeX = x
    this.data.homeY = y
    this.vx = 0
    this.vy = 0
  }

  private get awake(): boolean {
    return this.data.awake === true
  }

  /**
   * The heading it was placed with, which it faces while sitting on its post.
   *
   * The value arrives as a placement prop now — `objects.ts` declares `dir: -1`
   * as the registry default and `Level` forwards the merged props into this
   * factory — so the resting facing is a designer decision rather than a
   * constant baked into the sprite. The constructor has already snapped whatever
   * it was handed to exactly `1` or `-1`, which is why this getter is a plain
   * read: the `=== 1 ? 1 : -1` shape this used to have could only ever paper
   * over an `undefined` that should never reach it, and it papered over it by
   * choosing left.
   */
  private get dir(): 1 | -1 {
    return this.data.dir === 1 ? 1 : -1
  }

  private get phase(): number {
    return (this.data.phase as number) ?? 0
  }

  override update(level: LevelRuntime, player: PlayerLike): void {
    const dx = player.x + player.w / 2 - (this.x + this.w / 2)
    const dy = player.y + player.h / 2 - (this.y + this.h / 2)

    if (this.awake) {
      if (Math.abs(dx) > WISP_SLEEP_X) this.data.awake = false
    } else if (Math.abs(dx) <= WISP_WAKE_X && Math.abs(dy) <= WISP_WAKE_Y) {
      this.data.awake = true
    }

    if (this.awake) {
      const len = Math.hypot(dx, dy)
      if (len > 0.001) {
        this.vx = (dx / len) * WISP_SPEED
        this.vy = (dy / len) * WISP_SPEED
        this.facing = this.vx > 0 ? 1 : -1
      } else {
        this.vx = 0
        this.vy = 0
      }
    } else {
      this.goHome()
    }

    this.x += this.vx
    this.y += this.vy
    this.contain(level)
    this.data.phase = (this.phase + 1) % WISP_PHASE
  }

  /**
   * Steer back to the placed post and stop there.
   *
   * The reference game's Wisp does not wander off on its own: the moment it loses the player it
   * glides back to the spot it was placed on and hovers there until Tavi comes
   * back into range, which is what makes leaving one behind a way to *lose* it
   * rather than a way to relocate it. The version here fell back to an aimless
   * crawl along `dir` instead, so a Wisp that woke once never re-anchored — it
   * stayed parked wherever the player had last been and only ever chased from
   * there, and a course could be quietly emptied of the threat it was placed to
   * guard.
   */
  private goHome(): void {
    const hx = num(this.data, 'homeX', this.x)
    const hy = num(this.data, 'homeY', this.y)
    const dx = hx - this.x
    const dy = hy - this.y
    const len = Math.hypot(dx, dy)

    if (len <= WISP_HOME) {
      // Snap onto the post rather than merely getting close: a leftover
      // fraction of a pixel would keep re-entering the branch below every frame
      // and the wisp would never be properly still.
      this.x = hx
      this.y = hy
      this.vx = 0
      this.vy = 0
      // At rest it faces the heading it was placed with, which is what the
      // `dir` prop is for — a wisp that had just been chasing leftward does not
      // flip back the instant it stops.
      this.facing = this.dir
      return
    }

    // Never faster than the gap it has left, so it arrives instead of overshooting.
    const speed = Math.min(WISP_DRIFT, len)
    this.vx = (dx / len) * speed
    this.vy = (dy / len) * speed
    if (this.vx !== 0) this.facing = this.vx > 0 ? 1 : -1
  }

  /** Keep the wisp inside the course, bouncing off the invisible edge walls. */
  private contain(level: LevelRuntime): void {
    const maxX = level.width * TILE - this.w
    const maxY = level.height * TILE - this.h
    if (this.x < 0) {
      this.x = 0
      this.vx = Math.abs(this.vx)
    } else if (this.x > maxX) {
      this.x = maxX
      this.vx = -Math.abs(this.vx)
    }
    if (this.y < 0) {
      this.y = 0
      this.vy = Math.abs(this.vy)
    } else if (this.y > maxY) {
      this.y = maxY
      this.vy = -Math.abs(this.vy)
    }
  }

  onStomp(level: LevelRuntime, _player: PlayerLike): boolean {
    level.spawn('poof', this.x, this.y)
    this.alive = false
    awardKill(level, this)
    return true
  }

  onTouch(_level: LevelRuntime, player: PlayerLike): void {
    hurtPlayer(player)
  }
}

// ── pickups ────────────────────────────────────────────────────────────────

/**
 * Base for the items that push up out of a ? Block. For the first
 * `EMERGE_FRAMES` frames they rise exactly one tile under their own steam and
 * ignore gravity entirely; after that they hand over to their own behaviour
 * and are flagged `'move'`.
 */
abstract class BlockPickup extends EntityBase {
  constructor(kind: string, x: number, y: number) {
    super(kind, x, y, 16, 16)
    this.data.stage = 'emerge'
    this.data.rise = 0
    this.data.phase = 0
    this.vx = 0
    this.vy = 0
  }

  protected get phase(): number {
    return (this.data.phase as number) ?? 0
  }

  /**
   * Rise out of the block. Returns true while still emerging, i.e. while the
   * caller must skip its normal behaviour for this frame.
   */
  protected emerge(): boolean {
    if (this.data.stage === 'move') return false
    this.y -= EMERGE_SPEED
    const rise = num(this.data, 'rise') + 1
    this.data.rise = rise
    if (rise >= EMERGE_FRAMES) this.data.stage = 'move'
    return true
  }
}

/**
 * Expansion Cell — rises out of a ? Block, then strolls off at 0.5px a frame,
 * turning at walls and at the lip of a ledge but happily falling off anything
 * it is already airborne over. Touching it turns a small player big; a big Tavi
 * gets the points instead, which is what the placed `expansion-cell` object does too.
 * Renderer: `expansion-cell`, flipped by `facing`.
 */
class ExpansionCell extends BlockPickup {
  constructor(kind: string, x: number, y: number, released = false) {
    super(kind, x, y)
    this.facing = 1
    /** True when a block bump spawned it: see `update`. */
    this.data.released = released
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    if (this.emerge()) return
    const world = level
    // Only consult the ledge while the feet are planted, otherwise a cell
    // already falling would spin in mid-air instead of dropping.
    //
    // A cell *released from a block* deliberately steps off the ledge it landed
    // on: the block payout has to come down to the player who bumped it from
    // below, or the shelf it emerged onto becomes the only place it can ever be
    // collected. A placed cell keeps patrolling, so an authored pickup cannot
    // walk itself off a platform and into a pit.
    const turnAtLedges = this.data.released !== true
    if (
      this.wallAhead(world) ||
      (turnAtLedges && this.onGround && !this.groundAhead(world))
    ) {
      turnAround(this)
    }
    this.vx = this.facing * EXPANSION_SPEED
    step(this, world, WALK_GRAVITY, WALK_FRICTION)
  }

  onTouch(level: LevelRuntime, player: PlayerLike): void {
    if (!isBig(player)) player.grow()
    addScore(level, SCORE.powerup)
    sfxPower()
    spawnHudPopup(this.x + this.w / 2, this.y, `+${SCORE.powerup}`, '#fcfcfc')
    this.alive = false
  }
}

/**
 * Spare Battery — moves exactly like the Expansion Cell, but collecting it
 * grants a life rather than a power-up, so `onTouch` replaces the expansion-cell's
 * wholesale instead of appending to it.
 * Renderer: `spare-battery`, flipped by `facing`.
 */
class SpareBattery extends ExpansionCell {
  constructor(x: number, y: number, released = false) {
    super('spare-battery', x, y, released)
  }

  override onTouch(level: LevelRuntime, player: PlayerLike): void {
    addLife(level, player)
    addScore(level, SCORE.powerup)
    sfxPower()
    spawnHudPopup(this.x + this.w / 2, this.y, '1UP', '#85ded1')
    this.alive = false
  }
}

/**
 * Ember Cell — rises out of a ? Block and then sits on top of it, bobbing in
 * place. It never moves of its own accord, so it needs no physics at all.
 * Touching it is the *only* way to become fire-capable, which is why it cannot
 * be swallowed: the block is spent either way.
 * Renderer: `ember-cell`, floated vertically by `data.phase`.
 */
class EmberCell extends BlockPickup {
  constructor(x: number, y: number) {
    super('ember-cell', x, y)
  }

  override update(_level: LevelRuntime, _player: PlayerLike): void {
    if (this.emerge()) return
    this.vx = 0
    this.vy = 0
    this.data.phase = (this.phase + 1) % EMBER_CELL_PHASE
  }

  onTouch(level: LevelRuntime, player: PlayerLike): void {
    giveFire(player)
    addScore(level, SCORE.powerup)
    sfxPower()
    spawnHudPopup(this.x + this.w / 2, this.y, `+${SCORE.powerup}`, '#fcfcfc')
    this.alive = false
  }
}

/**
 * Shield Prism — rises out of a ? Block and then ricochets along the ground on a
 * fixed 3px hop, reversing at walls. Touching it buys a few seconds of
 * invincibility, during which contact with anything is a kill for the enemy
 * rather than for Tavi. `data.phase` toggles every frame so the renderer can
 * flash it.
 * Renderer: `prism`, flipped by `facing`, hidden on alternate `data.phase`.
 */
class Prism extends BlockPickup {
  constructor(x: number, y: number) {
    super('prism', x, y)
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    if (this.emerge()) return
    const world = level
    this.data.phase = (this.phase + 1) % 2
    if (wallInFace(this, world)) turnAround(this)
    this.vx = this.facing * PRISM_SPEED
    step(this, world, WALK_GRAVITY, WALK_FRICTION)
    // Land, then immediately hop again — the prism is never at rest.
    if (this.onGround) this.vy = PRISM_HOP
  }

  onTouch(level: LevelRuntime, player: PlayerLike): void {
    giveShield(player)
    addScore(level, SCORE.powerup)
    sfxPower()
    spawnHudPopup(this.x + this.w / 2, this.y, `+${SCORE.powerup}`, '#fcfcfc')
    this.alive = false
  }
}

/**
 * Coin — a static collectible, bobbing one pixel up and down on a slow sine.
 * Collecting it banks a coin and 200 points.
 * Renderer: `coin`, nudged vertically by `data.phase`.
 */
class Coin extends EntityBase {
  constructor(x: number, y: number) {
    super('coin', x, y, 10, 14)
    this.data.baseY = y
    this.data.phase = 0
  }

  private get phase(): number {
    return (this.data.phase as number) ?? 0
  }

  override update(_level: LevelRuntime, _player: PlayerLike): void {
    const t = (this.phase + 1) / COIN_PERIOD
    this.data.phase = (this.phase + 1) % COIN_PERIOD
    this.y =
      num(this.data, 'baseY') - Math.round(Math.sin(t * Math.PI * 2) * COIN_BOB)
  }

  onTouch(level: LevelRuntime, _player: PlayerLike): void {
    level.addCoin()
    addScore(level, SCORE.coin)
    sfxCoin()
    spawnHudPopup(this.x + this.w / 2, this.y, `+${SCORE.coin}`, '#fcd800')
    this.alive = false
  }
}

/**
 * Coin Pop — the little coin that leaps out of a bumped ? Block. It ignores
 * terrain, arcs up under its own light gravity, spins, and vanishes after half
 * a second. (Banking the coin is the block's job, not this one's.)
 * Renderer: `coin`, flipped on `data.life` for the spin.
 */
class CoinPop extends EntityBase {
  constructor(x: number, y: number) {
    super('coin-pop', x, y, 10, 14)
    this.vy = COIN_POP_LAUNCH
    this.data.life = 0
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    const life = num(this.data, 'life') + 1
    this.data.life = life
    this.vy += COIN_POP_GRAVITY
    this.y += this.vy
    if (life >= COIN_POP_LIFE || this.y > level.height * TILE)
      this.alive = false
  }
}

// ── projectiles ────────────────────────────────────────────────────────────

/**
 * Fireball — a cannon shot. It crosses the screen at 3px a frame, bounces off
 * the floor, ricochets off walls a couple of times and then fizzles out, and
 * it hurts the player on contact. A friendly fireball (the player's own throw,
 * built with `props.friendly`) is the same projectile minus the damage: it
 * ricochets back into the thrower all the time, and in the originals your own
 * shot never hurts you.
 * Renderer: `fireball`, flipped by `facing`.
 */
class Fireball extends EntityBase {
  constructor(x: number, y: number, facing: 1 | -1, friendly = false) {
    super('fireball', x, y, 8, 8)
    this.facing = facing
    this.vx = facing * FIREBALL_SPEED
    this.vy = 0
    this.data.bounces = 0
    this.data.life = 0
    this.data.friendly = friendly
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    const world = level
    const life = num(this.data, 'life') + 1
    this.data.life = life
    if (life > FIREBALL_LIFE) {
      this.alive = false
      return
    }
    // Ricochet as soon as the leading edge touches a wall, rather than turning
    // two tiles early the way a walker does.
    if (wallInFace(this, world)) {
      this.vx = -this.vx
      this.facing = this.vx > 0 ? 1 : -1
      const bounces = num(this.data, 'bounces') + 1
      this.data.bounces = bounces
      if (bounces > FIREBALL_BOUNCES) {
        this.alive = false
        return
      }
    }
    this.vx = this.facing * FIREBALL_SPEED
    // No friction: the bounce impulse is a velocity, and must survive the step.
    step(this, world, WALK_GRAVITY, 0)
    if (this.onGround) this.vy = FIREBALL_BOUNCE
  }

  onTouch(_level: LevelRuntime, player: PlayerLike): void {
    // The player's own throw ricochets back across him; the originals never
    // punished that. A cannon's shot is unmarked and still hurts.
    if (this.data.friendly === true) return
    hurtPlayer(player)
  }
}

// ── particles ──────────────────────────────────────────────────────────────

/**
 * Brick Debris — one of the four shards thrown out when a brick is broken. Each
 * shard is spawned with its own sideways velocity, tumbles as it falls under
 * heavier-than-normal gravity, and is culled once it drops out of the course.
 * Renderer: `debris`, rotated by `data.angle` (which advances by `data.spin`).
 */
class Debris extends EntityBase {
  constructor(x: number, y: number, props: Record<string, unknown>) {
    super('debris', x, y, 8, 8)
    // The broken-brick behaviour gives each shard its own sideways speed. With
    // none supplied, fan them out from the uid instead — but only on the first
    // update, because the factory stamps the uid after construction.
    this.data.customVx = typeof props.vx === 'number'
    this.vx = num(props, 'vx', 0)
    this.vy = -DEBRIS_LAUNCH
    this.data.angle = 0
    this.data.spin = num(props, 'spin', DEBRIS_SPIN)
    this.data.life = 0
  }

  override update(level: LevelRuntime, _player: PlayerLike): void {
    if (this.data.customVx !== true) {
      const spread = 0.8 + (this.uid % 4) * 0.4
      this.vx = this.uid % 2 === 0 ? spread : -spread
    }
    const life = num(this.data, 'life') + 1
    this.data.life = life
    this.data.angle = (num(this.data, 'angle') + num(this.data, 'spin')) % 360
    step(this, level, DEBRIS_GRAVITY, 0)
    if (life > DEBRIS_LIFE || this.y > level.height * TILE) this.alive = false
  }
}

/**
 * Rivet Squash — the flattened sprite a stomped rivet leaves behind. It does
 * not move at all; it just lies there and then fades out.
 * Renderer: `rivet-squash`, faded out as `data.life` approaches 30.
 */
class RivetSquash extends EntityBase {
  constructor(x: number, y: number) {
    super('rivet-squash', x, y, 16, 8)
    this.vx = 0
    this.vy = 0
    this.data.life = 0
  }

  override update(_level: LevelRuntime, _player: PlayerLike): void {
    const life = num(this.data, 'life') + 1
    this.data.life = life
    if (life > SQUASH_LIFE) this.alive = false
  }
}

/**
 * Poof — the soft expanding puff that marks an enemy defeat or a prism sparkle.
 * It has no collision response whatsoever; it grows about its own centre for
 * twelve frames and then pops out of existence.
 * Renderer: `drawPoof` grows a translucent disc from `data.life`.
 */
class Poof extends EntityBase {
  constructor(x: number, y: number) {
    super('poof', x, y, POOF_START, POOF_START)
    this.data.cx = x + POOF_START / 2
    this.data.cy = y + POOF_START / 2
    this.data.life = 0
  }

  override update(_level: LevelRuntime, _player: PlayerLike): void {
    const life = num(this.data, 'life') + 1
    this.data.life = life
    const t = Math.min(1, life / POOF_FRAMES)
    const size = POOF_START + (POOF_END - POOF_START) * t
    this.w = size
    this.h = size
    this.x = num(this.data, 'cx') - size / 2
    this.y = num(this.data, 'cy') - size / 2
    if (life >= POOF_FRAMES) this.alive = false
  }
}

// ── registration ───────────────────────────────────────────────────────────

registerEntity('rivet', (x, y, _props, uid) => withUid(new Rivet(x, y), uid))

registerEntity('scout', (x, y, _props, uid) =>
  withUid(new Scout('scout', x, y, true), uid),
)

registerEntity('wanderer', (x, y, _props, uid) =>
  withUid(new Wanderer(x, y), uid),
)

registerEntity('shardling', (x, y, _props, uid) =>
  withUid(new Shardling(x, y), uid),
)

registerEntity('wisp', (x, y, props, uid) =>
  withUid(new Wisp(x, y, props), uid),
)

registerEntity('expansion-cell', (x, y, props, uid) =>
  withUid(
    new ExpansionCell('expansion-cell', x, y, props.released === true),
    uid,
  ),
)

registerEntity('ember-cell', (x, y, _props, uid) =>
  withUid(new EmberCell(x, y), uid),
)

registerEntity('prism', (x, y, _props, uid) => withUid(new Prism(x, y), uid))

registerEntity('spare-battery', (x, y, props, uid) =>
  withUid(new SpareBattery(x, y, props.released === true), uid),
)

registerEntity('coin', (x, y, _props, uid) => withUid(new Coin(x, y), uid))

registerEntity('coin-pop', (x, y, _props, uid) =>
  withUid(new CoinPop(x, y), uid),
)

registerEntity('fireball', (x, y, props, uid) =>
  withUid(
    new Fireball(
      x,
      y,
      num(props, 'facing', 1) >= 0 ? 1 : -1,
      props.friendly === true,
    ),
    uid,
  ),
)

registerEntity('debris', (x, y, props, uid) =>
  withUid(new Debris(x, y, props), uid),
)

registerEntity('rivet-squash', (x, y, _props, uid) =>
  withUid(new RivetSquash(x, y), uid),
)

registerEntity('poof', (x, y, _props, uid) => withUid(new Poof(x, y), uid))
